import { create } from 'zustand';

// Per-tab operator presence for the alert host (docs/verticals/operator-notifications.md
// §1a decision 3, §7.5): `away` = the tab is hidden OR 2 minutes passed without user
// input (pointer / key / touch / wheel). Network activity NEVER counts as presence — a
// socket push or a fetch is the console working, not the operator looking.
//
// One idle timer, reset on input (re-armed at most once a second under a moving
// pointer; when it fires it re-checks the last input and re-arms for what is left), and
// passive listeners. No polling: nothing here runs while the operator is idle except the
// one timer that ends the presence.

export type Presence = 'present' | 'away';

/** Without input for this long, the operator is away (PO 2026-10-28). */
export const IDLE_MS = 120_000;

const INPUT = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'] as const;

export interface IPresenceState {
  presence: Presence;
  /** When the current state began — for `away`, the last input (or the hide). */
  since: number;
}

export const usePresenceStore = create<IPresenceState>(() => ({
  presence: 'present',
  since: Date.now(),
}));

/** Watch this document's presence; returns the teardown. One watcher per tab. */
export function watchPresence(doc: Document = document): () => void {
  const set = (presence: Presence, since: number): void => {
    if (usePresenceStore.getState().presence !== presence) {
      usePresenceStore.setState({ presence, since });
    }
  };
  let last = Date.now();
  let armed = last;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = (ms: number): void => {
    clearTimeout(timer);
    timer = setTimeout(idle, ms);
  };
  function idle(): void {
    const left = last + IDLE_MS - Date.now();
    if (left > 0) arm(left);
    else set('away', last);
  }
  const input = (): void => {
    last = Date.now();
    if (doc.visibilityState === 'hidden') return;
    set('present', last);
    if (last - armed >= 1_000) {
      armed = last;
      arm(IDLE_MS);
    }
  };
  // Hidden → away now; shown again → that is the operator coming back (an input).
  const visibility = (): void => {
    if (doc.visibilityState === 'hidden') set('away', Date.now());
    else input();
  };
  const opts = { passive: true, capture: true } as const;
  for (const type of INPUT) doc.addEventListener(type, input, opts);
  doc.addEventListener('visibilitychange', visibility);
  usePresenceStore.setState({
    presence: doc.visibilityState === 'hidden' ? 'away' : 'present',
    since: last,
  });
  arm(IDLE_MS);
  return () => {
    clearTimeout(timer);
    for (const type of INPUT) doc.removeEventListener(type, input, opts);
    doc.removeEventListener('visibilitychange', visibility);
  };
}
